import React, { useEffect, useState } from 'react';
import { Text, IColumn } from '@fluentui/react';
import TableDefaultComponent from '../table-default/table-default.component';
import ErrorMessageComponent from '../error-message/error-message.component';
import NoPermissionComponent from '../no-permission/no-permission.component';
import ItemActividadUltimosEventos from '../../models/ItemActividadUltimosEventos';

import ApiService from '../../services/api/api.service';
import useAuthStatus from '../../hooks/auth/AuthHook';

import ItemPermiso from '../../models/ItemPermiso';
import ElementEnum from '../../enums/ElementEnum';

const CHAR_WIDTH = 8;
const HEADER_PADDING = 16;

const calcColumnWidth = (header: string, values: string[]): number => {
    const headerWidth = header.length * CHAR_WIDTH + HEADER_PADDING;
    const maxDataWidth = values.reduce((max, val) => {
        const w = (val || '').length * CHAR_WIDTH;
        return w > max ? w : max;
    }, 0);
    return Math.max(headerWidth, maxDataWidth);
};

const buildColumns = (data: ItemActividadUltimosEventos[]): IColumn[] => [
    {
        key: '1',
        name: 'Descripción',
        fieldName: 'Descripción',
        minWidth: calcColumnWidth('Descripción', data.map(d => d.Descripción)),
        isResizable: true,
        flexGrow: 1,
    },
    {
        key: '2',
        name: 'Para',
        fieldName: 'Para',
        minWidth: calcColumnWidth('Para', data.map(d => d.Para)),
        maxWidth: calcColumnWidth('Para', data.map(d => d.Para)),
        isResizable: true,
    },
    {
        key: '3',
        name: 'Cliente',
        fieldName: 'Cliente',
        minWidth: calcColumnWidth('Cliente', data.map(d => d.Cliente)),
        maxWidth: calcColumnWidth('Cliente', data.map(d => d.Cliente)),
        isResizable: true,
    },
    {
        key: '4',
        name: 'Fecha',
        fieldName: 'Fecha',
        minWidth: calcColumnWidth('Fecha', data.map(d => d.Fecha)),
        maxWidth: calcColumnWidth('Fecha', data.map(d => d.Fecha)),
        isResizable: true,
    },
];

interface ILastEventsTableProps {
    updateTime: number;
    onLoading: () => void;
    onLoaded: () => void;
    onSelected: (item: ItemActividadUltimosEventos) => void;
};

const LastEventsTableComponent = (props: ILastEventsTableProps) : JSX.Element => {
    const auth = useAuthStatus();
    const workOrdersPermission : ItemPermiso = auth.getPermission(ElementEnum.WORK_ORDERS);

    const [lastEvents, setLastEvents] = useState<ItemActividadUltimosEventos[]>([]);
    const [columns, setColumns] = useState<IColumn[]>(buildColumns([]));
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);

    const onLoading = props.onLoading;
    const onLoaded = props.onLoaded;

    useEffect(() => {
        let timer : NodeJS.Timeout;
        const getData = (showLoader: boolean) : void => {
            if (auth.user && workOrdersPermission.Ver) {
                if (showLoader) {
                    setLoading(true);
                }
                setError('');
                ApiService.Post<ItemActividadUltimosEventos[]>("ResumenActividad", "GetUltimasOrdenesTrabajo", auth.user).then((response: ItemActividadUltimosEventos[]) => {
                    timer = setTimeout(() => {
                        getData(false);
                    }, props.updateTime * 1000);
                    setLastEvents(response);
                    setColumns(buildColumns(response));
                    setLoading(false);
                }, (error: string) => {
                    timer = setTimeout(() => {
                        getData(false);
                    }, props.updateTime * 1000);
                    setError(error);
                    setLoading(false);
                });
            }
            else {
                setLoading(false);
            }
        }

        getData(true);

        return () => {
            setLastEvents([]);
            setLoading(false);
            setError('');
            clearTimeout(timer);
        };
    }, [props.updateTime, workOrdersPermission.Ver, auth.user]);

    useEffect(() => {
        if (!loading) {
            onLoaded();
        }
        else {
            onLoading();
        }
    }, [loading, onLoaded, onLoading]);

    const onRowClick = (item: ItemActividadUltimosEventos) : void => {
        props.onSelected(item);
    };

    return (
        workOrdersPermission.Ver ? (
            <>
                <Text variant='large'>
                    Recientes
                </Text>
                <TableDefaultComponent
                    noRowsMessage={(!loading && !error) ? 'No hay eventos recientes' : undefined}
                    items={lastEvents}
                    columns={columns}
                    onRowClick={onRowClick} />
                <ErrorMessageComponent message={error} />
            </>
        ) : (
            <NoPermissionComponent showButton={false} />
        )
    );
};

export default LastEventsTableComponent;
